import Dexie, { Table } from 'dexie';
import { Intervention } from '../domain/planning/intervention';
import { Document } from '../domain/Shared/document';
import { SANDRETaxionModel } from '../models/SANDRE-taxon-model';
import { FileUtility } from '../tools/file-utility';
import { EDocumentType } from '../tools/ui/mutilple-file-upload-box/document-type';
const IDBExportImport = require('indexeddb-export-import');

export interface InterventionEntity {
    Id?: number;
    Code: string;
    PictureEntityIds?: number[];
}

export interface PictureEntity {
    Id?: number;
    Name: string;
    DocumentType: number;
    UIDisabled: boolean;
    Description: string;
    Data?: string;
}

export interface UserEntity {
    Id?: number;
    Code: string;
    EimasId: string;
    Email: string;
    Fullname: string;
    Habilitation?: string;
    IsActive: boolean
}

export interface SondeEntity {
    Id?: number;
    Code: string;
    FacteurCorrection: string;
    Reference: string;
    Type: number;
    IsActive: boolean;
}

export interface IBMRTaxonEntity {
    Id?: number;
    Code: number;
    CodeIBMR: string;
    Famille: string;
    Genre: string
    Instruction: string;
}

export class AppDB extends Dexie {
    public interventions!: Table<InterventionEntity, number>;
    public pictures!: Table<PictureEntity, number>;
    public users!: Table<UserEntity, number>;
    public sondes!: Table<SondeEntity, number>;
    public IBMRTaxons!: Table<IBMRTaxonEntity, number>;

    constructor() {
        super('HEBI2');
        this.version(6).stores({
            interventions: '++Id, Code, RedactorPUID, *PictureEntityIds',
            pictures: '++Id, DocumentType, UIDisabled, Description, Data',
            users: '++Id, Code, EimasId, Email, Fullname , Habilitation, IsActive',
            sondes: '++Id, Code, FacteurCorrection, Reference, Type, IsActive',
            IBMRTaxons: '++Id, Code, CodeIBMR, Famille, Genre, Instruction'
        });

        // this.on('populate', () => this.populate()); // populate event happens at the first opening of the db
        this.open()
            .catch(e => {
                return this.handleError(e);
            });
    }

    async populate() {
        // await db.transaction('rw', db.interventions, db.pictures, async () => {

        //     const lastRiskId = await db.pictures.bulkAdd([
        //         {
        //             Code: 'RCode1',
        //             Name: 'Rat',
        //             PictogramName: 'ATEX'
        //         },
        //         {
        //             Code: 'RCode2',
        //             Name: 'Raton',
        //             PictogramName: 'ATEX'
        //         },
        //         {
        //             Code: 'RCode4',
        //             Name: 'Ratoune',
        //             PictogramName: 'ATEX'
        //         }
        //     ]);
        //     const lastIdentifiedRIskId = await db.pictures.bulkAdd([
        //         {
        //             // riskAnalysisId,
        //             Position: 1,
        //             Comment: 'Feed the birds',
        //             // otherMeanOfPreventionDescription: ''
        //             RiskId: lastRiskId - 2
        //         },
        //         {
        //             // riskAnalysisId,
        //             Position: 2,
        //             Comment: 'Watch a movie',
        //             RiskId: lastRiskId - 1
        //         },
        //         {
        //             // riskAnalysisId,
        //             Position: 3,
        //             Comment: 'Have some sleep',
        //             RiskId: lastRiskId
        //         }
        //     ]);

        //     const ra1 = await db.riskAnalysis.add({
        //         Code: 'RACocode1',
        //         Status: 1,
        //         IdentifiedRiskEntityIds: [lastIdentifiedRIskId - 2, lastIdentifiedRIskId - 1]
        //     });
        //     const ra2 = await db.riskAnalysis.add({
        //         Code: 'RACocode2',
        //         Status: 1,
        //         IdentifiedRiskEntityIds: [lastIdentifiedRIskId]
        //     });

        //     await db.interventions.add({
        //         Code: 1234,
        //         RedactorPUID: 'FNR8',
        //         RiskAnalysisId: ra1
        //     });

        //     await db.interventions.add({
        //         Code: 12345,
        //         RedactorPUID: 'HE4I',
        //         RiskAnalysisId: ra2
        //     });
        // });
    }

    async resetDatabase() {
        await db.transaction('rw', db.interventions, db.pictures, db.users, db.sondes, async () => {
            await this.interventions.clear();
            await this.pictures.clear();
            await this.users.clear();
            await this.sondes.clear();
            await this.IBMRTaxons.clear();
        });
    }

    async resetUserList() {
        await db.transaction('rw', db.users, db.pictures, async () => {
            await this.users.clear();
        });
    }

    async resetSondeList() {
        await db.transaction('rw', db.sondes, db.pictures, async () => {
            await this.sondes.clear();
        });
    }

    async resetTaxonList(list: SANDRETaxionModel[]) {
        await db.transaction('rw', db.IBMRTaxons, async () => {
            await this.IBMRTaxons.clear();
            
            if (list && list.length > 0) {
                for (let i = 0; i < list.length; i++) {
                    await this.addTaxon(list[i]);
                }
            }
        });
    }

    async addIntervention(ie: InterventionEntity) {
        const interventionId = await db.interventions.add({
            Code: ie.Code,
        });
        return interventionId;
    }

    async getInterventionByCode(code: string) {
        return await db.interventions.where('Code').equals(code).first(async ie => {

            const intervention = Object.assign(new Intervention(), ie);
            if (ie) {
                intervention.Documents = await this.getPictureList(ie);
            }

            return intervention;
        });
    }

    async getInterventionList() {
        const interventionList = await Promise.all(
            await db.interventions.toArray(ies => {
                return ies.map(async ie => {
                    const intervention = Object.assign(new Intervention(), ie);
                    if (ie) {
                        intervention.Documents = await this.getPictureList(ie);
                    }
                    return intervention;
                });
            })
        );
        return interventionList;
    }

    async getPictureByType(ie: InterventionEntity, type: EDocumentType) {
        return await db.pictures.where('Id').anyOf(ie.PictureEntityIds).and(x => { return x.DocumentType === type }).first(picture => {
            return Object.assign(picture);
        })
    }

    async getPictureList(ie: InterventionEntity) {
        if (!ie.PictureEntityIds)
            return;
        return await db.pictures.where('Id').anyOf(ie.PictureEntityIds).toArray(async pictures => {
            const list: Document[] = [];

            for (let i = 0; i < pictures.length; i++) {

                const pe = pictures[i];
                const p = Object.assign(new Document(), pe);
                if (pe.Data)
                    p.File = FileUtility.dataURLtoFile(`${pe.Data}`, `${pe.Name}`);

                list.push(p);
            }

            return list;
        });
    }

    mapDocumentToPictureEntity(docType: EDocumentType, desc: string, data: string, name: string) {
        const pe = {} as PictureEntity;
        pe.Data = data;
        pe.DocumentType = docType;
        pe.UIDisabled = false;
        pe.Description = desc;
        pe.Name = name;
        return pe;
    }

    async attachPictureToIntervention(ie: InterventionEntity, pe: PictureEntity) {
        const pictureId = await db.pictures.add({
            Name: pe.Name,
            DocumentType: pe.DocumentType,
            UIDisabled: pe.Data === undefined,
            Description: pe.Description,
            Data: pe.Data !== undefined ? encodeURI(pe.Data) : undefined
        }).catch(e => {
            return this.handleError(e);
        });

        await db.interventions.update(ie.Id, {
            PictureEntityIds: ie.PictureEntityIds ? [...ie.PictureEntityIds, pictureId] : [pictureId]
        });

        return pictureId;
    }

    async deleteIntervention(code: string) {
        await db.interventions.where('Code').equals(code).delete();
    }

    async deleteStationMap(ie: InterventionEntity) {
        if (!ie.PictureEntityIds) {
            return;
        }
        return await db.pictures.where('Id').anyOf(ie.PictureEntityIds).toArray(async pictures => {

            for (let i = 0; i < pictures.length; i++) {

                if (pictures[i].DocumentType === EDocumentType.StationMap) {
                    await db.pictures.where('Id').equals(pictures[i].Id).delete();
                }
            }
        });
    }

    async deleteDocument(ie: InterventionEntity, dType: EDocumentType) {
        return await db.pictures.where('Id').anyOf(ie.PictureEntityIds).toArray(async pictures => {

            for (let i = 0; i < pictures.length; i++) {

                if (pictures[i].DocumentType === dType) {
                    await db.pictures.where('Id').equals(pictures[i].Id).delete();
                }
            }
        });
    }

    async deleteAllPicturesForAnIntervention(ie: InterventionEntity) {
        if (!ie.PictureEntityIds) {
            return;
        }

        await db.pictures.where('Id').anyOf(ie.PictureEntityIds).delete()
        // await db.pictures.bulkDelete(ie.PictureEntityIds);
        // await db.pictures.bulkDelete(await db.pictures.where('Id').anyOf(ie.PictureEntityIds).toArray(async pictures => {
        //     return pictures.map(x => x.Id);
        // }))
    }

    async addUser(ue: UserEntity) {
        const userId = await db.users.add({
            Code: ue.Code,
            EimasId: ue.EimasId,
            Email: ue.Email,
            Fullname: ue.Fullname,
            Habilitation: ue.Habilitation,
            IsActive: ue.IsActive,
        });
        return userId;
    }

    async getAllUsers() {
        return await db.users.toArray();
    }

    async addSonde(se: SondeEntity) {
        const sondeId = await db.sondes.add({
            Code: se.Code,
            FacteurCorrection: se.FacteurCorrection,
            Reference: se.Reference,
            Type: se.Type,
            IsActive: se.IsActive
        });
        return sondeId;
    }

    async getAllSondes() {
        return await db.sondes.toArray();
    }

    async addTaxon(taxon: SANDRETaxionModel) {
        const taxonId = await db.IBMRTaxons.add({
            Code: taxon.Code,
            CodeIBMR: taxon.CodeIBMR,
            Famille: taxon.Famille,
            Genre: taxon.Genre,
            Instruction: taxon.Instruction,
        });
        return taxonId;
    }

    async getTaxonByFilter(s: string) {
        // const list = await db.IBMRTaxons.filter(x => {
        //     return x.CodeIBMR.toLowerCase() === s.toLowerCase() || x.Genre.toLowerCase().includes(s.toLowerCase())
        // }).toArray();
        const list = await db.IBMRTaxons.where("CodeIBMR").startsWithIgnoreCase(s).or("Genre").startsWithIgnoreCase(s).toArray();
        const m = list.map(y => Object.assign(new SANDRETaxionModel, y)) as SANDRETaxionModel[];
        return m;
    }
    
    async exportDB(cb) {
        const idbDatabase = db.backendDB();
        IDBExportImport.exportToJsonString(idbDatabase, cb);
    }

    importDB(jsonString) {
        const idbDatabase = db.backendDB();
        IDBExportImport.clearDatabase(idbDatabase, function (err) {
            if (!err) {
                IDBExportImport.importFromJsonString(idbDatabase, jsonString, function (err) {
                    if (!err) {
                        console.log('Imported data successfully');
                    }
                });
            }
        });
    }

    handleError(e) {
        switch (e.name) {
            case 'AbortError':
                if (e.inner) {
                    return this.handleError(e.inner);
                }
                console.error('Abort error ' + e.message);
                break;
            case 'QuotaExceededError':
                console.error('QuotaExceededError ' + e.message);
                break;
            default:
                console.error(e);
                break;
        }
    }
}

export const db = new AppDB();

